// Admin · Testimonials
// Curate per-property guest testimonials. Each property starts with the
// shared default list; saving replaces the list for that property only.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Save, RefreshCw, Plus, Trash2, Loader2,
  CheckCircle2, AlertCircle, ArrowUp, ArrowDown,
} from "lucide-react";
import AdminAuthGate, { adminHeaders } from "@/components/admin/AdminAuthGate";
import { HOTELS } from "@/lib/data";
import { devLog } from "../lib/devLog";

const API = process.env.REACT_APP_BACKEND_URL || "";
const LANGS = [
  { id: "en", label: "EN" },
  { id: "fr", label: "FR" },
  { id: "es", label: "ES" },
];
const blankI18n = () => ({ en: "", fr: "", es: "" });
const blankItem = () => ({
  id: `local-${Math.random().toString(36).slice(2, 10)}`,
  name: "",
  place: "",
  travel_type: blankI18n(),
  text: blankI18n(),
});

const I18nField = ({ label, value, onChange, rows = 3, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1.5">{label}</p>
    <div className="space-y-2">
      {LANGS.map((l) => (
        <label key={l.id} className="flex gap-2 items-start">
          <span className="mt-2.5 inline-block w-7 text-[10px] tracking-[0.2em] uppercase text-[#C16542] font-medium">
            {l.label}
          </span>
          {rows === 1 ? (
            <input
              value={value?.[l.id] || ""}
              onChange={(e) => onChange({ ...(value || blankI18n()), [l.id]: e.target.value })}
              className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
              data-testid={`${testid}-${l.id}`}
            />
          ) : (
            <textarea
              value={value?.[l.id] || ""}
              onChange={(e) => onChange({ ...(value || blankI18n()), [l.id]: e.target.value })}
              rows={rows}
              className="flex-1 border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
              data-testid={`${testid}-${l.id}`}
            />
          )}
        </label>
      ))}
    </div>
  </div>
);

const TestimonialsEditor = ({ slug }) => {
  const [items, setItems] = useState([]);
  const [isOverride, setIsOverride] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [status, setStatus] = useState(null); // { type, msg }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/testimonials/${slug}`, {
      credentials: "include", headers: adminHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
      setIsOverride(!!data.is_override);
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Failed to load" });
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const updateItem = (idx, patch) =>
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) =>
    setItems((cur) => cur.filter((_, i) => i !== idx));
  const addItem = () => setItems((cur) => [...cur, blankItem()]);
  const moveItem = (idx, dir) => {
    setItems((cur) => {
      const next = [...cur];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return cur;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/admin/testimonials/${slug}`, {
      credentials: "include",
        method: "PUT",
        headers: { ...adminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        let detail = `HTTP ${res.status}`;
        try { const j = await res.json(); detail = j.detail || detail; } catch (err) { devLog.debug("[AdminTestimonials] parse error body failed", err); /* noop */ }
        throw new Error(detail);
      }
      const data = await res.json();
      setItems(data.items);
      setIsOverride(!!data.is_override);
      setStatus({ type: "ok", msg: "Saved." });
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Save failed" });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setResetting(true);
    setStatus(null);
    try {
      const res = await fetch(`${API}/api/admin/testimonials/${slug}`, {
      credentials: "include",
        method: "DELETE",
        headers: adminHeaders(),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
      setStatus({ type: "ok", msg: "Reset to shared defaults." });
    } catch (e) {
      setStatus({ type: "error", msg: e.message || "Reset failed" });
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[#5C5248] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading testimonials…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="testimonials-editor">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">
          {isOverride ? `Custom list (${items.length})` : `Using shared defaults (${items.length})`}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={addItem}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.22em] uppercase text-[#C16542] border border-[#C16542]/40 hover:bg-[#C16542] hover:text-white transition-colors"
            data-testid="testimonials-add"
          >
            <Plus className="w-3.5 h-3.5" /> Add quote
          </button>
          <button
            type="button"
            onClick={resetToDefaults}
            disabled={resetting || !isOverride}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.22em] uppercase text-[#5C5248] border border-[#2C2621]/15 hover:border-[#2C2621]/40 disabled:opacity-40"
            data-testid="testimonials-reset"
          >
            {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Reset
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[11px] tracking-[0.22em] uppercase text-white bg-[#C16542] hover:bg-[#a3502f] disabled:opacity-50"
            data-testid="testimonials-save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save
          </button>
        </div>
      </div>

      {status ? (
        <div
          className={`flex items-center gap-2 px-3 py-2 text-sm border ${
            status.type === "ok"
              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
              : "text-red-700 bg-red-50 border-red-200"
          }`}
          data-testid="testimonials-status"
        >
          {status.type === "ok" ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {status.msg}
        </div>
      ) : null}

      {items.length === 0 ? (
        <p className="text-sm text-[#5C5248] italic">
          No quotes yet — click <strong>Add quote</strong> to compose the list for this property.
        </p>
      ) : (
        <ul className="space-y-4">
          {items.map((it, idx) => (
            <li
              key={it.id || `idx-${idx}`}
              className="border border-[#2C2621]/12 bg-white p-5 space-y-4"
              data-testid={`testimonial-row-${idx}`}
            >
              <div className="flex flex-wrap gap-3 items-start justify-between">
                <div className="flex-1 grid sm:grid-cols-2 gap-3">
                  <label className="block">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1.5 block">Name</span>
                    <input
                      value={it.name || ""}
                      onChange={(e) => updateItem(idx, { name: e.target.value })}
                      placeholder="Sophie M."
                      className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
                      data-testid={`testimonial-name-${idx}`}
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248] mb-1.5 block">Place (optional)</span>
                    <input
                      value={it.place || ""}
                      onChange={(e) => updateItem(idx, { place: e.target.value })}
                      placeholder="Paris, France"
                      className="w-full border border-[#2C2621]/15 px-3 py-2 text-sm focus:outline-none focus:border-[#C16542] bg-white"
                      data-testid={`testimonial-place-${idx}`}
                    />
                  </label>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => moveItem(idx, -1)}
                    disabled={idx === 0}
                    className="w-8 h-8 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-30"
                    aria-label="Move up"
                    data-testid={`testimonial-up-${idx}`}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveItem(idx, 1)}
                    disabled={idx === items.length - 1}
                    className="w-8 h-8 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542] disabled:opacity-30"
                    aria-label="Move down"
                    data-testid={`testimonial-down-${idx}`}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="w-8 h-8 inline-flex items-center justify-center border border-[#2C2621]/15 text-[#5C5248] hover:border-red-500 hover:text-red-500"
                    aria-label="Delete testimonial"
                    data-testid={`testimonial-delete-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <I18nField
                label="Travel type (optional)"
                value={it.travel_type}
                onChange={(v) => updateItem(idx, { travel_type: v })}
                rows={1}
                testid={`testimonial-traveltype-${idx}`}
              />
              <I18nField
                label="Quote *"
                value={it.text}
                onChange={(v) => updateItem(idx, { text: v })}
                rows={3}
                testid={`testimonial-text-${idx}`}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AdminTestimonialsPageInner = () => {
  const properties = useMemo(() => HOTELS.filter((h) => !h.coming_soon), []);
  const [activeSlug, setActiveSlug] = useState(properties[0]?.slug || "");

  return (
    <div className="min-h-screen bg-[#FBF6EE] text-[#2C2621]" data-testid="admin-testimonials-page">
      <header className="bg-white border-b border-[#2C2621]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542]"
            data-testid="admin-back"
          >
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
          <h1 className="font-serif-x text-2xl">Testimonials</h1>
          <span className="text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">
            {properties.length} properties
          </span>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid md:grid-cols-12 gap-8">
        <aside className="md:col-span-4 lg:col-span-3">
          <nav className="bg-white border border-[#2C2621]/10 divide-y divide-[#2C2621]/10" data-testid="admin-testimonials-property-list">
            {properties.map((h) => {
              const isOn = h.slug === activeSlug;
              return (
                <button
                  type="button"
                  key={h.slug}
                  onClick={() => setActiveSlug(h.slug)}
                  className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                    isOn ? "bg-[#C16542] text-white" : "hover:bg-[#FBF6EE] text-[#2C2621]"
                  }`}
                  data-testid={`admin-testimonials-property-${h.slug}`}
                >
                  <div className="font-medium leading-tight">{h.name}</div>
                  <div className={`text-[10px] tracking-[0.22em] uppercase mt-1 ${isOn ? "text-white/80" : "text-[#5C5248]"}`}>
                    {h.location}
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        <section className="md:col-span-8 lg:col-span-9">
          {activeSlug ? (
            <TestimonialsEditor key={activeSlug} slug={activeSlug} />
          ) : (
            <p className="text-sm text-[#5C5248]">Select a property on the left.</p>
          )}
        </section>
      </div>
    </div>
  );
};

const AdminTestimonialsPage = () => (
  <AdminAuthGate>
    <AdminTestimonialsPageInner />
  </AdminAuthGate>
);

export default AdminTestimonialsPage;
